import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import Container from "../../components/Container";
import Page from "../../components/Page";
import Spinner from "../../components/ui/spinner";
import { adminWorkspacesApi } from "../api/workspaces";
import { adminUsersApi } from "../api/users";
import { adminEmployeesApi } from "../api/employees";
import { adminQualityBandsApi } from "../api/qualityBands";
import { adminAgentActionsApi } from "../api/agentActions";
import { adminConnectedPlatformsApi } from "../api/connectedPlatforms";
import { ApiError } from "../api/utils";
import type {
  AdminAgentAction,
  AdminConnectedPlatform,
  AdminEmployee,
  AdminQualityBand,
  AdminUserRecord,
  AdminWorkspace,
} from "../modules/types";

type PageData = {
  workspace: AdminWorkspace;
  users: AdminUserRecord[];
  employees: AdminEmployee[];
  qualityBands: AdminQualityBand[];
  agentActions: AdminAgentAction[];
  connectedPlatforms: AdminConnectedPlatform[];
};

const WorkspacePage: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<PageData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let isMounted = true;

    Promise.all([
      adminWorkspacesApi.getById(id),
      adminUsersApi.getAll(id),
      adminEmployeesApi.getAll(id),
      adminQualityBandsApi.getAll(id),
      adminAgentActionsApi.getAll(id),
      adminConnectedPlatformsApi.getAll(id),
    ])
      .then(
        ([
          workspace,
          users,
          employees,
          qualityBands,
          agentActions,
          connectedPlatforms,
        ]) => {
          if (!isMounted) return;
          setData({
            workspace,
            users,
            employees,
            qualityBands,
            agentActions,
            connectedPlatforms,
          });
        },
      )
      .catch((err: unknown) => {
        if (!isMounted) return;
        setError(
          err instanceof ApiError ? err.message : "Failed to load workspace",
        );
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (error) {
    return (
      <Page>
        <Container>
          <div className="bg-destructive/10 border border-destructive/40 px-4 py-3 rounded">
            <Text variant="sm" className="text-destructive">{error}</Text>
          </div>
        </Container>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page>
        <Container>
          <div className="flex justify-center py-12">
            <Spinner size={32} className="text-muted-foreground" />
          </div>
        </Container>
      </Page>
    );
  }

  const {
    workspace,
    users,
    employees,
    qualityBands,
    agentActions,
    connectedPlatforms,
  } = data;
  const sortedQualityBands = [...qualityBands].sort(
    (a, b) => a.lowerBound - b.lowerBound,
  );
  const sortedAgentActions = [...agentActions].sort((a, b) =>
    a.actionKey.localeCompare(b.actionKey),
  );

  return (
    <Page>
      <Container>
        <Link
          to="/admin/workspaces"
          className="text-muted-foreground text-sm hover:underline mb-2 inline-block"
        >
          &larr; All workspaces
        </Link>
        <Heading level={1} variant="lg">{workspace.name}</Heading>
        <Text variant="xl" className="text-muted-foreground mb-6">
          {workspace.slug}
        </Text>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Users ({users.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <Text variant="sm" className="text-muted-foreground">No users.</Text>
              ) : (
                <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2">
                  <Text variant="sm" className="font-semibold">Email</Text>
                  <Text variant="sm" className="font-semibold">Created</Text>
                  {users.map((user) => (
                    <React.Fragment key={user.id}>
                      <Text variant="sm">{user.email}</Text>
                      <Text variant="sm" className="text-muted-foreground">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Text>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Employees ({employees.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {employees.length === 0 ? (
                <Text variant="sm" className="text-muted-foreground">No employees.</Text>
              ) : (
                <div className="grid grid-cols-[1fr_1fr_auto] gap-x-6 gap-y-2">
                  <Text variant="sm" className="font-semibold">Name</Text>
                  <Text variant="sm" className="font-semibold">Email</Text>
                  <Text variant="sm" className="font-semibold">Scoring</Text>
                  {employees.map((employee) => (
                    <React.Fragment key={employee.id}>
                      <Link
                        to={`/admin/workspaces/${workspace.id}/employees/${employee.id}`}
                        className="text-sm hover:underline"
                      >
                        {employee.name}
                      </Link>
                      <Text variant="sm" className="text-muted-foreground">
                        {employee.email}
                      </Text>
                      <Text variant="sm" className="text-muted-foreground">
                        {employee.isScoring ? "Yes" : "No"}
                      </Text>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Scoring</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Heading level={3} variant="sm" className="mb-2">Quality Bands</Heading>
                {sortedQualityBands.length === 0 ? (
                  <Text variant="sm" className="text-muted-foreground">
                    No quality bands configured.
                  </Text>
                ) : (
                  <div className="grid grid-cols-[1fr_5rem] gap-x-6 gap-y-2">
                    <Text variant="sm" className="font-semibold">Lower bound</Text>
                    <Text variant="sm" className="font-semibold">Points</Text>
                    {sortedQualityBands.map((band) => (
                      <React.Fragment key={band.id}>
                        <Text variant="sm">{band.lowerBound}%</Text>
                        <Text variant="sm">{band.score}</Text>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Heading level={3} variant="sm" className="mb-2">Agent Actions</Heading>
                {sortedAgentActions.length === 0 ? (
                  <Text variant="sm" className="text-muted-foreground">
                    No agent actions configured.
                  </Text>
                ) : (
                  <div className="grid grid-cols-[1fr_5rem] gap-x-6 gap-y-2">
                    <Text variant="sm" className="font-semibold">Action</Text>
                    <Text variant="sm" className="font-semibold">Points</Text>
                    {sortedAgentActions.map((action) => (
                      <React.Fragment key={action.id}>
                        <Text variant="sm">{action.actionKey}</Text>
                        <Text variant="sm">{action.points}</Text>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Connected Platforms ({connectedPlatforms.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {connectedPlatforms.length === 0 ? (
                <Text variant="sm" className="text-muted-foreground">
                  No connected platforms.
                </Text>
              ) : (
                <div className="grid grid-cols-[1fr_1fr_auto] gap-x-6 gap-y-2">
                  <Text variant="sm" className="font-semibold">Provider</Text>
                  <Text variant="sm" className="font-semibold">Expires</Text>
                  <Text variant="sm" className="font-semibold">Status</Text>
                  {connectedPlatforms.map((platform) => (
                    <React.Fragment key={platform.id}>
                      <Text variant="sm">{platform.provider}</Text>
                      <Text variant="sm" className="text-muted-foreground">
                        {platform.expiresAt
                          ? new Date(platform.expiresAt).toLocaleDateString()
                          : "\u2014"}
                      </Text>
                      <Text variant="sm" className="text-muted-foreground">
                        {platform.revokedAt ? "Revoked" : "Active"}
                      </Text>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </Container>
    </Page>
  );
};

export default WorkspacePage;
